import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface MacOSNotesLayoutProps {
  sidebar: React.ReactNode;
  notesList: React.ReactNode;
  noteContent?: React.ReactNode;
  isEditing?: boolean;
  onToggleEditing?: () => void;
  sidebarCollapsed?: boolean;
  className?: string;
}

type PanelView = 'sidebar' | 'notes' | 'content';

export function MacOSNotesLayout({
  sidebar,
  notesList,
  noteContent,
  isEditing = false,
  onToggleEditing,
  sidebarCollapsed = false,
  className
}: MacOSNotesLayoutProps) {
  const isMobile = useIsMobile();
  const [currentPanel, setCurrentPanel] = useState<PanelView>('notes');

  if (isMobile) {
    return (
      <div className={cn("h-full flex flex-col", className)}>
        {/* Mobile Navigation */}
        <div className="flex border-b border-border/20">
          <button
            onClick={() => setCurrentPanel('sidebar')}
            className={cn(
              "flex-1 px-4 py-2 text-sm font-medium transition-colors",
              currentPanel === 'sidebar'
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Browse
          </button>
          <button
            onClick={() => setCurrentPanel('notes')}
            className={cn(
              "flex-1 px-4 py-2 text-sm font-medium transition-colors",
              currentPanel === 'notes'
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Notes
          </button>
          {noteContent && (
            <button
              onClick={() => setCurrentPanel('content')}
              className={cn(
                "flex-1 px-4 py-2 text-sm font-medium transition-colors",
                currentPanel === 'content'
                  ? "text-primary border-b-2 border-primary bg-primary/5"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Content
            </button>
          )}
        </div>

        {/* Mobile Panel Content */}
        <div className="flex-1 overflow-hidden">
          {currentPanel === 'sidebar' && (
            <div className="h-full">{sidebar}</div>
          )}
          {currentPanel === 'notes' && (
            <div className="h-full">{notesList}</div>
          )}
          {currentPanel === 'content' && noteContent && (
            <div className="h-full">{noteContent}</div>
          )}
        </div>
      </div>
    );
  }

  // Desktop three-panel layout with smooth animations
  return (
    <div className={cn("h-full flex relative overflow-hidden", className)}>
      {/* Left Sidebar - Dynamic width based on collapse state */}
      <div className={cn(
        "border-r border-border/20 flex-shrink-0 transition-all duration-500 ease-in-out",
        isEditing ? "w-0 -translate-x-full opacity-0" : "translate-x-0 opacity-100"
      )}>
        {sidebar}
      </div>

      {/* Middle Panel - Notes List - Adjusts based on sidebar state */}
      <div className={cn(
        "border-r border-border/20 bg-background/10 flex-shrink-0 transition-all duration-500 ease-in-out",
        isEditing 
          ? "w-0 -translate-x-full opacity-0" 
          : noteContent 
            ? "w-80 translate-x-0 opacity-100" 
            : "flex-1 translate-x-0 opacity-100"
      )}>
        <div className={cn(noteContent ? "w-80" : "w-full", "h-full")}>
          {notesList}
        </div>
      </div>

      {/* Collapse Toggle Button - Shows when editing - positioned to not overlap content */}
      {isEditing && (
        <div className="absolute left-2 top-2 z-50">
          <button
            onClick={onToggleEditing}
            className="w-8 h-8 bg-background/90 hover:bg-background border border-border/50 rounded-md backdrop-blur-sm transition-all duration-200 flex items-center justify-center text-foreground hover:text-primary shadow-sm"
            title="Show sidebar"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      )}

      {/* Right Panel - Note Content - Expands to full width when editing */}
      {noteContent && (
        <div className={cn(
          "bg-background/5 transition-all duration-500 ease-in-out relative min-h-0",
          isEditing ? "flex-1 pl-12" : "flex-1",
          isEditing && "pt-2" // Add top padding when editing to avoid hamburger overlap
        )}>
          <div className="h-full flex flex-col min-h-0 overflow-hidden">
            <div className="flex-1 min-h-0 overflow-auto">
              {noteContent}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}